import { useMemo } from "react";
import { useMotivoDemissaoListaViewModel } from "../../viewModels/motivo_demissao_lista_view_model";
import { GenerateMotivoDemissaoColumns } from "./columns";
import { Link, Breadcrumbs, Typography, Stack, TablePagination } from "@mui/material";
import { Header } from "./components/header";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { MotivoDemissao } from "../../../domain/models/motivo_demissao";
import { CadastroMotivoDemissaoModal } from "./components/cadastro_motivo_demissao_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * MotivoDemissaoLista
 */

export const MotivoDemissaoLista = () => {
  const viewModel = useMotivoDemissaoListaViewModel();

  const columnsDef = useMemo(() => {
    return GenerateMotivoDemissaoColumns({
      onActionEdit: viewModel.handleMotivoDemissaoCadastroOpenState,
      onActionDelete: viewModel.handleMotivoDemissaoDeleteOpenState,
    });
  }, [
    viewModel.handleMotivoDemissaoCadastroOpenState,
    viewModel.handleMotivoDemissaoDeleteOpenState,
  ]);

  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="w-[120px] ">
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                onClick={() => viewModel.handleMotivoDemissaoCadastroOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Motivo Demissão
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Motivo Demissão</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  tipoPesquisa={viewModel.filters.tipoPesquisa}
                  motivoDemissao={viewModel.filters.motivoDemissao}
                  status={viewModel.filters.status}
                  onTipoPesquisaChange={viewModel.handleTipoPesquisaChange}
                  onMotivoDemissaoChange={viewModel.handleMotivoDemissaoChange}
                  onStatusChange={viewModel.handleStatusChange} />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col flex-wrap justify-end">
                      <LegendComponent>
                        <LegendItemComponent
                          icon={<LegendIcon color='green' title='A' />}
                          label='Ativo' />

                        <LegendItemComponent
                          icon={<LegendIcon color='red' title='I' />}
                          label='Inativo' />

                      </LegendComponent>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="motivo_demissao_lista"
                    disableColumnMenu={false}
                    getRowId={(row: MotivoDemissao) => row.idMotivoDemissao}
                    columns={columnsDef}
                    loading={viewModel.loading}
                    rows={viewModel.motivosDemissao}
                    rowCount={viewModel.motivosDemissao.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }} />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage > 0 ? viewModel.pagination.rowsPerPage : 25}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }

        {/**
        * Modal.
        */}

        <CadastroMotivoDemissaoModal
          open={viewModel.openCadastroModal}
          saving={viewModel.saving}
          selectedMotivoDemissao={viewModel.selectedMotivoDemissao ?? null}
          onSubmit={viewModel.handleSubmitMotivoDemissao}
          onOpenChangeHistory={viewModel.handleOpenChangeHistory}
          onClose={() => viewModel.handleMotivoDemissaoCadastroOpenState(null, false)} />

        <DeleteDialog
          open={viewModel.openDeleteModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleMotivoDemissaoDeleteOpenState(null, false)}
          title={`Você realmente deseja excluir este item?`}
          onDelete={viewModel.handleConfirmarDeletarMotivoDemissao} />

        {/**
        * Modal historico de alterações.
        */}

        <ChangeHistoryModal
          changeHistoryHandler={viewModel.changeHistory} />

      </div>
    </>
  );
}